import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import {
  type CreateTriggerV2Request,
  type TriggerV2Record,
  type UpdateTriggerV2Request,
} from "../TriggerV2Record";

import { forgetTrigger, storeTrigger } from "./triggerCache";

// The mutations below don't wait for the list's refetch: they settle as soon
// as the server confirms, which the cache write already shows.

export function useCreateTriggerV2(orgId?: string) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateTriggerV2Request) =>
      triggerV2Service.createTrigger(request, resolvedOrgId),
    onSuccess: (record: TriggerV2Record) => {
      void storeTrigger(queryClient, resolvedOrgId, record.name, record);
    },
  });
}

export function useUpdateTriggerV2(orgId?: string) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name,
      updates,
    }: {
      name: string;
      updates: UpdateTriggerV2Request;
    }) => triggerV2Service.updateTrigger(name, updates, resolvedOrgId),
    onSuccess: (record: TriggerV2Record, { name }) => {
      void storeTrigger(queryClient, resolvedOrgId, name, record);
    },
  });
}

/**
 * Mutates a trigger's enabled/disabled state when toggle is called
 */
export function useToggleTrigger(
  trigger: TriggerV2Record,
  onError: (error: Error) => void,
): { checked: boolean; pending: boolean; toggle: (enabled: boolean) => void } {
  const { triggerV2Service } = useDomainServices();
  const queryClient = useQueryClient();

  const { mutate, isPending, variables } = useMutation({
    mutationFn: (enabled: boolean) =>
      triggerV2Service.setEnabled(trigger.name, enabled, trigger.org_id),
    onSuccess: (record: TriggerV2Record) => {
      void storeTrigger(queryClient, trigger.org_id, trigger.name, record);
    },
    onError,
  });
  return {
    checked: isPending ? variables : trigger.enabled,
    pending: isPending,
    toggle: mutate,
  };
}

export function useDeleteTriggerV2(orgId?: string) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) =>
      triggerV2Service.deleteTrigger(name, resolvedOrgId),
    onSuccess: (_, { name }) => {
      void forgetTrigger(queryClient, resolvedOrgId, name);
    },
  });
}
